import { Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { FormGroup, Label } from 'reactstrap';
import * as yup from 'yup';
import { TEAM_ROLES, VIEWER_ROLE } from '../../constants/teamRoles';
import { useRemainingPaidSeats, useUserRole } from '../../utils/hooks';
import { getTeamBillingPath } from '../../utils/team';
import Endash from '../basics/Endash';
import {
  DropdownButtonField,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from '../templates/template-components';
import useMutateWithState from '../../hooks/useMutateWithState';

const teamMemberSchema = yup.object().shape({
  email: yup.string().email().required('Required'),
  role: yup.string().required('Required'),
});

const defaultTeamMember = {
  email: '',
  role: '',
};

const TeamMemberForm = ({ mutation, submitButtonText, team, teamMember }) => {
  const navigate = useNavigate();
  const { isSuperadmin } = useUserRole();
  const [{ loading }, mutateAndSetState] = useMutateWithState();
  const member = teamMember || defaultTeamMember;
  const { id, updatedAt } = team;
  const { remainingPaidSeats } = useRemainingPaidSeats(id);
  const roles =
    remainingPaidSeats > 0 || isSuperadmin ? TEAM_ROLES : VIEWER_ROLE;

  return (
    <Formik
      initialValues={member}
      validationSchema={teamMemberSchema}
      onSubmit={async (values) =>
        mutateAndSetState(
          () =>
            mutation({
              variables: {
                input: {
                  id,
                  updatedAt,
                  members: values,
                },
              },
            }),
          'Team member added!',
        ).then(({ data: { upsertTeamMembers } }) => {
          if (upsertTeamMembers?.result && !upsertTeamMembers?.errors) {
            navigate('..', { relative: 'path' });
          }
        })
      }
    >
      {({ isSubmitting }) => (
        <TemplateForm>
          <ValidatedInput
            type="email"
            name="email"
            label="Email"
            placeholder="Email Address"
          />
          <FormGroup>
            <Label>Permissions</Label>
            <DropdownButtonField
              inModal
              outline
              size="sm"
              options={roles}
              name="role"
            />
          </FormGroup>
          <FormGroup>
            <SubmitButton disabled={isSubmitting || loading}>
              {submitButtonText}
            </SubmitButton>
          </FormGroup>
          <FormGroup>
            {remainingPaidSeats <= 0 && (
              <p>
                You have reached the maximum number of editors
                {team?.allowedActions?.canUpdateSettings && (
                  <span>
                    <Endash />
                    <Link to={getTeamBillingPath(team)}>Edit subscription</Link>
                  </span>
                )}
              </p>
            )}
          </FormGroup>
        </TemplateForm>
      )}
    </Formik>
  );
};

export default TeamMemberForm;
